import {
  BookOpenIcon,
  ChatAlt2Icon,
  ClipboardCheckIcon,
  ClockIcon,
  CreditCardIcon,
  DatabaseIcon,
  FilterIcon,
  GlobeAltIcon,
  InboxInIcon,
  PaperClipIcon,
  PencilAltIcon,
  ReplyIcon,
  ShieldCheckIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import CustomerDataApps from '../mocks/CustomerDataApps';
import EmailThread from '../mocks/EmailThread';
import MailboxSettings from '../mocks/MailboxSettings';
import type { ChannelContent, DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';

const BRAND: DemoBrand = {
  name: 'Alder & Co.',
  initial: 'A',
  bg: 'bg-[#15803d]',
};

/*
 * Facts this page relies on (checked 2026-10-21, docs/notes/agents-channels.md
 * and docs/agents/email.mdx): email, Gmail and Outlook are paid channels;
 * Gmail/Outlook replies go in the same thread from the mailbox's address,
 * as drafts by default ("Save a draft reply", Recommended) or sent; only
 * mail that arrives after connecting is answered; automated mail is skipped
 * on both paths; attachments aren't opened; customer data apps are
 * read-only, owner-chosen, and replies that used them are held for review
 * by default; Gmail/Outlook threads show in Conversations under Email. The
 * forwarding channel gives the agent its own address and can send from a
 * verified domain. "Learn from my edits" is left out: its migration may
 * not be live.
 */
export function useEmailContent(): ChannelContent {
  const { t } = useChannelsT();
  const { t: tc } = useLocale('chatbots');

  return {
    key: 'email',
    name: t('Email'),
    logo: 'FaEnvelope',
    seoTitle: t('AI Email Responder: An AI Agent That Answers Customer Emails'),
    seoDescription: t(
      'An AI email responder for Gmail and Outlook. It answers customers in the same thread from your own knowledge, as drafts you check or sent for you.',
    ),
    hero: {
      label: t('AI email responder'),
      title: t('Every customer email, answered.'),
      accent: t('Right in your Gmail or Outlook.'),
      lead: t(
        'Connect your inbox and your AI agent replies to customers in the same thread, from what you’ve taught it. Start with drafts you check, and let it send when you’re ready.',
      ),
      facts: [
        t('Gmail and Outlook'),
        t('Drafts first, sending when you choose'),
        t('Email on paid plans'),
      ],
      description: t(
        'Example: a customer emails a shop to swap a rain jacket for a bigger size. The agent checks the exchange policy and saves a reply as a draft in the same thread, ready to send.',
      ),
      demo: (
        <EmailThread
          brand={BRAND}
          subject={t('Swap for a bigger size?')}
          customer={{ name: 'Ava Brooks', email: 'ava@example.com' }}
          body={t(
            'Hi! The rain jacket I ordered last week is too small. Can I swap it for a size L?',
          )}
          reply={t(
            'Hi Ava, of course! Exchanges are free within 30 days. Open the returns form on our website, choose Exchange and pick size L. We’ll send the new jacket as soon as the old one is on its way.',
          )}
          signature={t('Best wishes,')}
        />
      ),
      steps: [
        {
          icon: BookOpenIcon,
          title: t('Read the exchange policy'),
          via: tc('Knowledge'),
          at: 1300,
        },
        {
          icon: PencilAltIcon,
          title: t('Saved a draft in the thread'),
          via: 'Gmail',
          at: 1900,
        },
      ],
    },
    jobs: {
      title: t('Your support inbox, handled.'),
      tail: t('Without losing the personal touch.'),
      lead: t(
        'Most customer emails ask the same things. Your agent answers them in your tone, from your policies and help pages, and leaves the tricky ones to you.',
      ),
      items: [
        {
          icon: ChatAlt2Icon,
          title: t('Answers questions'),
          detail: t(
            'Orders, returns, pricing and policies, straight from your website and documents.',
          ),
        },
        {
          icon: ReplyIcon,
          title: t('Replies in the thread'),
          detail: t(
            'Writes back in the same conversation, from your own address, with your signature.',
          ),
        },
        {
          icon: DatabaseIcon,
          title: t('Looks customers up'),
          detail: t(
            'Checks the sender in apps you connect, such as Stripe or your database, without changing anything.',
          ),
        },
        {
          icon: ClipboardCheckIcon,
          title: t('Follows your rules'),
          detail: t(
            'Email instructions set the tone, what to promise and when to leave a draft for you.',
          ),
        },
        {
          icon: GlobeAltIcon,
          title: t('Speaks their language'),
          detail: t(
            'Replies in the language each customer writes in, from the same knowledge.',
          ),
        },
        {
          icon: ClockIcon,
          title: t('Never misses a message'),
          detail: t(
            'New email is checked every minute, and each reply lands in its own thread.',
          ),
        },
      ],
    },
    setup: {
      title: t('Connect your inbox in a minute.'),
      tail: t('Gmail, Outlook or forwarding.'),
      steps: [
        {
          title: t('Build your agent'),
          detail: t(
            'Add your help pages, policies and documents, and write how your replies should sound.',
          ),
        },
        {
          title: t('Connect your inbox'),
          detail: t(
            'Sign in with Gmail or Outlook. Rather not connect a mailbox? Give the agent its own address and forward your support email to it.',
          ),
        },
        {
          title: t('Choose drafts or sending'),
          detail: t(
            'Start with drafts you check in your inbox, then let it send once its replies look right.',
          ),
        },
      ],
      guide: 'https://easy-peasy.ai/docs/agents/email',
      guideLabel: t('Read the email setup guide'),
      description: t(
        'The agent’s Gmail channel with a connected support mailbox and "Save a draft reply" chosen for new emails, next to the option to connect Outlook.',
      ),
      visual: <MailboxSettings address='support@example.com' />,
    },
    spotlight: {
      title: t('It looks before it writes.'),
      tail: t('In the apps you already use.'),
      lead: t(
        'Connect apps like Stripe, Supabase or PostHog, and before it replies in Gmail or Outlook, the agent checks the sender there. It only reads, and replies that used app data wait for your review.',
      ),
      points: [
        t('Read-only: it never changes anything'),
        t('Lookups by the sender’s email address'),
        t('Replies that used app data wait for review'),
        t('You choose which apps each agent may use'),
      ],
      description: t(
        'The agent’s customer data apps, Stripe, Supabase and PostHog, each marked read-only, and a reply about a refund saved as a draft for review after the agent looked in Stripe.',
      ),
      visual: (
        <CustomerDataApps
          subject={t('Re: Where’s my refund?')}
          apps={[
            { name: 'Stripe', mark: 'S', color: 'bg-[#635bff]' },
            { name: 'Supabase', mark: 'S', color: 'bg-[#249361]' },
            { name: 'PostHog', mark: 'P', color: 'bg-[#f54e00]' },
          ]}
        />
      ),
    },
    rules: {
      title: t('Good to know.'),
      tail: t('Email, in plain words.'),
      items: [
        {
          icon: CreditCardIcon,
          title: t('Paid plans'),
          detail: t(
            'Email, Gmail and Outlook are on paid plans, and you can build and test your agent for free first.',
          ),
        },
        {
          icon: PencilAltIcon,
          title: t('Drafts until you say so'),
          detail: t(
            'In Gmail and Outlook it saves drafts by default. Switch to sending once its replies look right.',
          ),
        },
        {
          icon: InboxInIcon,
          title: t('New email only'),
          detail: t(
            'It answers email that arrives after you connect, never your backlog.',
          ),
        },
        {
          icon: FilterIcon,
          title: t('No replies to robots'),
          detail: t(
            'Auto-replies, out-of-office messages, mailing lists, bounces and spam are skipped.',
          ),
        },
        {
          icon: PaperClipIcon,
          title: t('Text, not attachments'),
          detail: t(
            'It reads the text of each email. Attachments aren’t opened.',
          ),
        },
        {
          icon: ShieldCheckIcon,
          title: t('Read-only lookups'),
          detail: t(
            'Connected apps are only read, never changed, and replies that used them wait for your review by default.',
          ),
        },
      ],
    },
    faqs: [
      {
        question: t('Can it reply from my own Gmail or Outlook address?'),
        answer: t(
          'Yes. Connect a Gmail, Google Workspace, Outlook or Microsoft 365 inbox and it replies in the same thread from that address, as a draft or sent, your choice.',
        ),
      },
      {
        question: t('Will it send emails without me checking?'),
        answer: t(
          'Only if you switch on sending. Drafts are the default. Even with sending on, it saves a draft when a safety check fails, and you can tell it to save one whenever it isn’t sure.',
        ),
      },
      {
        question: t('Can it look up a customer’s order or account?'),
        answer: t(
          'In Gmail and Outlook replies, yes. Connect apps such as Stripe, Supabase or PostHog and it checks the sender there, read-only. Replies that used app data wait for your review by default.',
        ),
      },
      {
        question: t('What if I don’t want to connect my inbox?'),
        answer: t(
          'Give the agent its own email address and forward your support mailbox to it. Replies come from our mail servers with your name, or from your own domain once you verify it.',
        ),
      },
      {
        question: t('Does it answer my old emails?'),
        answer: t(
          'No. It only answers email that arrives after you connect it or switch it back on.',
        ),
      },
      {
        question: t('Does it reply to newsletters and auto-replies?'),
        answer: t(
          'No. Auto-replies, out-of-office messages, mailing lists, bounces and spam are skipped, so it never gets stuck replying to machines.',
        ),
      },
      {
        question: t('Where can I see what it replied?'),
        answer: t(
          'Gmail and Outlook threads appear in Conversations under Email, with each draft or sent reply and the apps it looked in.',
        ),
      },
      {
        question: t('Which languages does it speak?'),
        answer: t(
          'It replies in the language each customer writes in, more than 80 of them, from the same knowledge.',
        ),
      },
    ],
    finalTitle: t('Your inbox could be answering itself by lunchtime.'),
    finalExchange: {
      question: t('Do you ship to Canada?'),
      answer: t('Yes, in 5 to 7 working days. Shipping is free over $100.'),
    },
  };
}
